import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  BarChart3,
  FileText,
  Mail,
  Download,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Calendar,
  RefreshCw,
  TrendingUp,
  Eye,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { LeadManagementService } from '../services/LeadManagementService';
import {
  MarketingLead,
  LeadType,
  LeadStatus,
  DashboardStats,
  LEAD_STATUS_LABELS,
  LEAD_STATUS_COLORS,
  LEAD_TYPE_LABELS,
  getLeadDisplayName,
  getLeadCompany,
  toDate,
} from '../types/leads';
import { useAdminAuth } from '../contexts/AdminContext';

const PIE_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#22c55e'];

const TAB_ITEMS = [
  { label: 'Dashboard', icon: BarChart3 },
  { label: 'Scorecard Leads', icon: FileText },
  { label: 'Founder Applications', icon: Users },
  { label: 'Contacts', icon: Mail },
];

function formatDate(val: Date | { toDate: () => Date } | undefined): string {
  const d = toDate(val);
  if (d.getTime() === 0) return '-';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getScoreBadgeClasses(score: number): string {
  if (score < 30) return 'bg-red-500/20 text-red-400 border border-red-500/30';
  if (score < 55) return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
  if (score < 80) return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
  return 'bg-green-500/20 text-green-400 border border-green-500/30';
}

const MarketingLeadsPage: React.FC = () => {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [scorecardLeads, setScorecardLeads] = useState<MarketingLead[]>([]);
  const [founderLeads, setFounderLeads] = useState<MarketingLead[]>([]);
  const [contactLeads, setContactLeads] = useState<MarketingLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set());

  // ---- Data fetching ----

  const loadDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await LeadManagementService.getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTabData = useCallback(async (tabIndex: number) => {
    if (loadedTabs.has(tabIndex)) return;
    try {
      setLoading(true);
      if (tabIndex === 1) {
        const leads = await LeadManagementService.getLeads('scorecard');
        setScorecardLeads(leads);
      } else if (tabIndex === 2) {
        const leads = await LeadManagementService.getLeads('founders');
        setFounderLeads(leads);
      } else if (tabIndex === 3) {
        const leads = await LeadManagementService.getLeads('contacts');
        setContactLeads(leads);
      }
      setLoadedTabs(prev => new Set(prev).add(tabIndex));
    } catch (err) {
      console.error('Error loading tab data:', err);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [loadedTabs]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  useEffect(() => {
    if (activeTab > 0) {
      loadTabData(activeTab);
    }
  }, [activeTab, loadTabData]);

  // ---- Handlers ----

  const handleToggleDemo = async (lead: MarketingLead) => {
    try {
      await LeadManagementService.toggleBookedDemo(
        lead.type,
        lead.id,
        !lead.bookedDemo,
        user?.email || 'admin'
      );
      setScorecardLeads(prev =>
        prev.map(l =>
          l.id === lead.id
            ? { ...l, bookedDemo: !l.bookedDemo, status: !l.bookedDemo && ['new', 'contacted'].includes(l.status) ? 'demo_booked' as LeadStatus : l.status }
            : l
        )
      );
      toast.success(`Demo ${!lead.bookedDemo ? 'booked' : 'unbooked'} for ${getLeadDisplayName(lead)}`);
    } catch (err) {
      console.error('Error toggling demo:', err);
      toast.error('Failed to update demo status');
    }
  };

  const handleToggleConverted = async (lead: MarketingLead) => {
    try {
      await LeadManagementService.toggleConverted(
        lead.type,
        lead.id,
        !lead.converted,
        user?.email || 'admin'
      );
      setScorecardLeads(prev =>
        prev.map(l =>
          l.id === lead.id
            ? { ...l, converted: !l.converted, status: !l.converted ? 'converted' as LeadStatus : l.status }
            : l
        )
      );
      toast.success(`${getLeadDisplayName(lead)} marked as ${!lead.converted ? 'converted' : 'not converted'}`);
    } catch (err) {
      console.error('Error toggling converted:', err);
      toast.error('Failed to update conversion status');
    }
  };

  const handleExportCSV = async () => {
    try {
      const csv = await LeadManagementService.exportToCSV('scorecard');
      if (!csv) {
        toast.error('No data to export');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `scorecard-leads-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      toast.error('Failed to export CSV');
    }
  };

  const handleRefresh = async () => {
    setLoadedTabs(new Set());
    if (activeTab === 0) {
      await loadDashboardStats();
    } else {
      setLoadedTabs(new Set());
      await loadTabData(activeTab);
    }
    toast.success('Data refreshed');
  };

  // ---- Filtering ----

  const filterLeads = (leads: MarketingLead[]): MarketingLead[] => {
    let filtered = leads;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => {
        const name = getLeadDisplayName(lead).toLowerCase();
        const email = (lead.email || '').toLowerCase();
        const company = getLeadCompany(lead).toLowerCase();
        return name.includes(q) || email.includes(q) || company.includes(q);
      });
    }
    if (statusFilter) {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }
    return filtered;
  };

  // ---- Chart data ----

  const sourceChartData = stats
    ? Object.entries(stats.bySource)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    : [];

  const typeChartData = stats
    ? Object.entries(stats.byType)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => ({
          name: LEAD_TYPE_LABELS[type as LeadType] || type,
          value: count,
        }))
    : [];

  // ---- Render helpers ----

  const renderStatusBadge = (status: LeadStatus) => {
    const color = LEAD_STATUS_COLORS[status] || '#6b7280';
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {LEAD_STATUS_LABELS[status] || status}
      </span>
    );
  };

  const renderLoadingSpinner = () => (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
        <p className="mt-4 text-slate-400">Loading...</p>
      </div>
    </div>
  );

  // ---- Tab: Dashboard ----

  const renderDashboard = () => {
    if (!stats) return renderLoadingSpinner();

    return (
      <div className="space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Total Leads</p>
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalLeads}</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Leads This Month</p>
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.leadsThisMonth}</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Demos Booked</p>
                <Eye className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.demosBooked}</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Conversion Rate</p>
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.conversionRate}%</p>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Breakdown Bar Chart */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Leads by Source</h3>
            {sourceChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#f8fafc',
                    }}
                  />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No source data available</p>
            )}
          </div>

          {/* Lead Type Pie Chart */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Lead Type Distribution</h3>
            {typeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={typeChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ stroke: '#64748b' }}
                  >
                    {typeChartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#f8fafc',
                    }}
                  />
                  <Legend
                    wrapperStyle={{ color: '#94a3b8', fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No type data available</p>
            )}
          </div>
        </div>

        {/* Recent Leads Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">Recent Leads</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {stats.recentLeads.map(lead => (
                  <tr
                    key={`${lead.type}-${lead.id}`}
                    onClick={() => navigate(`/leads/${lead.type}/${lead.id}`)}
                    className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white">{getLeadDisplayName(lead)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{LEAD_TYPE_LABELS[lead.type]}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.source?.utm_source || 'direct'}</td>
                    <td className="px-6 py-4 text-sm">{renderStatusBadge(lead.status || 'new')}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{formatDate(lead.timestamp)}</td>
                  </tr>
                ))}
                {stats.recentLeads.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                      No leads yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ---- Tab: Scorecard Leads ----

  const renderScorecardLeads = () => {
    const filtered = filterLeads(scorecardLeads);

    return (
      <div className="space-y-4">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search name, email, company..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as LeadStatus | '')}
                className="pl-10 pr-8 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              >
                <option value="">All Statuses</option>
                {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Score</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Tier</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Demo</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Converted</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filtered.map(lead => (
                  <tr
                    key={lead.id}
                    className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                  >
                    <td
                      className="px-6 py-4 text-sm text-white font-medium"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {getLeadDisplayName(lead)}
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-slate-300"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {getLeadCompany(lead)}
                    </td>
                    <td
                      className="px-6 py-4 text-sm"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {lead.score !== undefined ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getScoreBadgeClasses(lead.score)}`}>
                          {lead.score}
                        </span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td
                      className="px-6 py-4 text-sm"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {lead.tier ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-200">
                          {lead.tier}
                        </span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-slate-300"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {lead.source?.utm_source || 'direct'}
                    </td>
                    <td
                      className="px-6 py-4 text-sm"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {renderStatusBadge(lead.status || 'new')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleToggleDemo(lead);
                        }}
                        className="focus:outline-none"
                        title={lead.bookedDemo ? 'Unmark demo' : 'Mark demo booked'}
                      >
                        {lead.bookedDemo ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-500 hover:text-slate-300" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleToggleConverted(lead);
                        }}
                        className="focus:outline-none"
                        title={lead.converted ? 'Unmark converted' : 'Mark as converted'}
                      >
                        {lead.converted ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-500 hover:text-slate-300" />
                        )}
                      </button>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-slate-400"
                      onClick={() => navigate(`/leads/scorecard/${lead.id}`)}
                    >
                      {formatDate(lead.timestamp)}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                      {searchQuery || statusFilter ? 'No leads match your filters' : 'No scorecard leads yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ---- Tab: Founder Applications ----

  const renderFounderLeads = () => {
    const filtered = filterLeads(founderLeads);

    return (
      <div className="space-y-4">
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search name, email, company..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
          />
        </div>

        {/* Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Brokers</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Offices</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Tracking Method</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filtered.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/founders/${lead.id}`)}
                    className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white font-medium">{getLeadDisplayName(lead)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{getLeadCompany(lead)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.numberOfBrokers || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.numberOfOffices || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.currentTrackingMethod || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.source?.utm_source || 'direct'}</td>
                    <td className="px-6 py-4 text-sm">{renderStatusBadge(lead.status || 'new')}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{formatDate(lead.timestamp)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-400">
                      {searchQuery ? 'No leads match your search' : 'No founder applications yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ---- Tab: Contacts ----

  const renderContacts = () => {
    const filtered = filterLeads(contactLeads);

    return (
      <div className="space-y-4">
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search name, email, company..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
          />
        </div>

        {/* Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Message</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filtered.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/contacts/${lead.id}`)}
                    className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white font-medium">{getLeadDisplayName(lead)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.email}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{getLeadCompany(lead)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 max-w-xs">
                      {lead.message
                        ? lead.message.length > 80
                          ? `${lead.message.substring(0, 80)}...`
                          : lead.message
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">{lead.source?.utm_source || 'direct'}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{formatDate(lead.timestamp)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                      {searchQuery ? 'No contacts match your search' : 'No contact submissions yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ---- Main render ----

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header with gradient */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Marketing Leads</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Lead pipeline, source attribution, and conversion tracking
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Pipeline analytics</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">Source attribution</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="text-sm">CSV export</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation + Refresh */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
          {TAB_ITEMS.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.label}
                onClick={() => {
                  setActiveTab(index);
                  setSearchQuery('');
                  setStatusFilter('');
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === index
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tab Content */}
      {loading && !stats && activeTab === 0 && renderLoadingSpinner()}
      {loading && activeTab > 0 && !loadedTabs.has(activeTab) && renderLoadingSpinner()}

      {activeTab === 0 && stats && renderDashboard()}
      {activeTab === 1 && loadedTabs.has(1) && renderScorecardLeads()}
      {activeTab === 2 && loadedTabs.has(2) && renderFounderLeads()}
      {activeTab === 3 && loadedTabs.has(3) && renderContacts()}
    </div>
  );
};

export default MarketingLeadsPage;
